import { useMemo, useState } from "react";
import { Building2, CheckCheck, Inbox, Mail, Phone, Search } from "lucide-react";
import { eventsApi } from "@/services/eventsApi";

const formatDate = (value) => new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
const SERVICES = ["All services", "Production", "Automotive R&D", "Motorsports Management", "Gear & Apparel", "General"];
const RANGES = [["all", "All time"], ["7", "Last 7 days"], ["30", "Last 30 days"]];

export const InquiriesPanel = ({ inquiries, csrfToken, onChanged }) => {
  const [query, setQuery] = useState("");
  const [service, setService] = useState("All services");
  const [range, setRange] = useState("all");

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const cutoff = range === "all" ? null : Date.now() - Number(range) * 86400000;
    return inquiries.filter((item) => {
      if (service !== "All services" && item.service !== service) return false;
      if (cutoff && new Date(item.created_at).getTime() < cutoff) return false;
      if (term && ![item.name, item.email, item.company, item.message].some((field) => (field || "").toLowerCase().includes(term))) return false;
      return true;
    });
  }, [inquiries, query, service, range]);

  const markRead = async (id) => {
    try {
      await eventsApi.post(`/admin/inquiries/${id}/read`, {}, { headers: { "X-CSRF-Token": csrfToken } });
      onChanged();
    } catch { /* keep panel usable */ }
  };

  if (inquiries.length === 0) return <section className="admin-panel" data-testid="admin-inquiries-empty"><div className="admin-panel-empty"><Inbox/><h3>NO ENQUIRIES YET.</h3><p>Quote requests submitted on the website will land here instantly.</p></div></section>;
  return (
    <section className="admin-panel" data-testid="admin-inquiries-panel">
      <div className="admin-panel-heading"><div><p className="eyebrow">Quote requests</p><h2>ENQUIRY INBOX</h2></div><p>{inquiries.filter((item) => item.status === "new").length} unread of {inquiries.length}</p></div>
      <div className="inquiry-filters" data-testid="inquiry-filters">
        <label className="inquiry-search"><Search/><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, company, message..." data-testid="inquiry-search-input" /></label>
        <select value={service} onChange={(e) => setService(e.target.value)} data-testid="inquiry-service-filter">{SERVICES.map((item) => <option key={item}>{item}</option>)}</select>
        <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="inquiry-date-filter">{RANGES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
      </div>
      {filtered.length === 0 && <p className="admin-empty" data-testid="inquiry-no-matches">No enquiries match the current filters.</p>}
      <div className="inquiry-list">
        {filtered.map((item) => (
          <article key={item.id} className={item.status === "new" ? "inquiry-card is-new" : "inquiry-card"} data-testid={`inquiry-${item.id}`}>
            <header>
              <div><span className="inquiry-service">{item.service}</span>{item.status === "new" && <span className="inquiry-new-dot" data-testid={`inquiry-${item.id}-unread`}>New</span>}</div>
              <time>{formatDate(item.created_at)}</time>
            </header>
            <h3>{item.name}</h3>
            <div className="inquiry-contact">
              <a href={`mailto:${item.email}`} data-testid={`inquiry-${item.id}-email`}><Mail/> {item.email}</a>
              <a href={`tel:${item.phone.replace(/[^+0-9]/g, "")}`} data-testid={`inquiry-${item.id}-phone`}><Phone/> {item.phone}</a>
              {item.company && <span><Building2/> {item.company}</span>}
            </div>
            <p>{item.message}</p>
            {item.status === "new" && <button onClick={() => markRead(item.id)} data-testid={`inquiry-${item.id}-mark-read`}><CheckCheck/> Mark as read</button>}
          </article>
        ))}
      </div>
    </section>
  );
};
